/**
 * AddEditCardDeckV2 — V2 page chrome + form wrapper for Card Decks.
 * Combines the page chrome (sticky header) with the DeckFormV2.
 *
 * Same fetch / save graph as v1 (Pages/CardDeck/AddEditCardDeck.js):
 *   • GET /decks/{id} via apiV2 → deck state
 *   • saveCardDeck(deck, onSuccess, onError) → POST/PUT /decks
 *   • closeEditCardDeck(navigate) on cancel
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import DeckFormV2 from './v2/DeckFormV2';
import {
  closeEditCardDeck,
  saveCardDeck,
} from '../../../actions/cardDeckActions';
import { fetchAllCategories } from '../../../actions/categoryActions';
import { fetchAllSubCategories } from '../../../actions/subcategoryActions';
import { apiV2 } from '../../../actions/index';
import { Button, cn } from '../../redesign';

const DEFAULT_DECK = {
  name: '',
  description: '',
  category_id: '',
  primary_color_hex: '#FFFFFF',
  module_description: '',
  module_image_url: '',
  header_image_url: '',
  wrapper_image_url: '',
  search_result_image_url: '',
  share_link_description: '',
  share_image_url: '',
  purchase_store_text: '',
  purchase_store_url: '',
  see_all_description: '',
  sort_order: 0,
  free_view_limit: 1,
  is_premium: true,
  is_active: false,
};

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

export default function AddEditCardDeckV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [deck, setDeck] = useState(DEFAULT_DECK);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    setFetchLoading(true);
    apiV2.get(`/decks/${id}`)
      .then((response) => setDeck(response.data))
      .catch(() => navigate('/card-decks'))
      .finally(() => setFetchLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    dispatch(fetchAllCategories());
    dispatch(fetchAllSubCategories());
  }, [dispatch]);

  const handleSave = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setSaveLoading(true);
    dispatch(saveCardDeck(
      deck,
      () => {
        setSaveLoading(false);
        navigate('/card-decks');
      },
      () => setSaveLoading(false),
    ));
  };

  const handleCancel = () => {
    dispatch(closeEditCardDeck(() => navigate('/card-decks')));
  };

  const isLoading = fetchLoading || saveLoading;
  const eyebrow = isEdit ? 'Editing deck' : 'New deck';
  const title = isEdit ? (deck?.name || `Deck #${id}`) : 'Untitled deck';

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={isLoading} />
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !deck?.name && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div className="font-mono text-text-muted" style={{ fontSize: 12 }}>
              {isEdit ? (
                <>
                  deck_id · <strong className="text-navy">{id}</strong>
                </>
              ) : (
                <span>Fill the fields below and click Create.</span>
              )}
            </div>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Button variant="ghost" onClick={handleCancel} type="button">
              Discard
            </Button>
            <Button
              variant="coral"
              type="submit"
              form="deck-form"
              leadingIcon={SaveIconSvg}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        {fetchLoading ? (
          <div
            className="text-text-muted"
            style={{ padding: 24, textAlign: 'center', fontSize: 13 }}
          >
            Loading deck…
          </div>
        ) : (
          <form
            id="deck-form"
            onSubmit={handleSave}
            autoComplete="off"
            style={{ minWidth: 0, maxWidth: 1100 }}
          >
            <DeckFormV2 deck={deck} setDeck={setDeck} />
          </form>
        )}
      </div>
    </div>
  );
}
